import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { Upload, Camera, ArrowRight } from "lucide-react";
import axios from "axios";
import CameraCapture from "@/components/Camera";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_69566509-b9c0-48ae-b149-6b9accd67eee/artifacts/3nmi4pqp_Dynamic%20Logo%20with%20Sapphire%20Coral%20Gradient%20%288%29.png";

const Landing = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  const handleLogin = () => {
    // Navigate to login page which shows all options
    navigate("/login");
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate("/profile");
    } else {
      navigate("/login");
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const imageData = reader.result;
        // Always navigate to dashboard with the image - guests can use the app
        navigate("/dashboard", { state: { uploadedImage: imageData } });
      };
      reader.readAsDataURL(file);
    }
  }, [navigate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".webp"] },
    maxFiles: 1
  });

  // Handle camera capture
  const handleCameraCapture = (imageData) => {
    // Always navigate to dashboard with the image - guests can use the app
    navigate("/dashboard", { state: { uploadedImage: imageData } });
  };

  // Open camera
  const handleOpenCamera = () => {
    setIsCameraOpen(true);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-primary)' }}>
      {/* Navigation */}
      <header className="nav">
        <div className="nav-content">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="Shadesense.ai" className="h-10 w-auto" />
          </Link>
          
          <div className="flex items-center gap-6">
            {!isLoading && (
              isAuthenticated ? (
                <button 
                  onClick={() => navigate('/profile')}
                  className="btn-primary"
                  data-testid="go-to-profile-btn"
                >
                  My Profile
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => handleLogin()}
                    className="btn-secondary"
                    data-testid="login-btn"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="text-small hover:underline transition-opacity"
                    style={{ color: 'var(--text-muted)' }}
                    data-testid="continue-guest-btn"
                  >
                    or continue without an account
                  </button>
                </div>
              )
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main>
        <section className="container">
          <div className="flex flex-col items-center justify-center min-h-[calc(100vh-var(--nav-height)-200px)] py-16">
            
            {/* Editorial Copy */}
            <div className="text-center max-w-2xl mb-12 animate-fade-in">
              <p className="text-meta uppercase tracking-widest mb-6" style={{ color: 'var(--accent-primary)' }}>
                Your Personal Color Advisor
              </p>
              <h1 className="text-display mb-6" style={{ color: 'var(--text-primary)' }}>
                Let us help you discover perfectly matched clothing
              </h1>
              <p className="text-body text-secondary max-w-xl mx-auto">
                Upload a photo of your clothing to reveal its precise color palette and discover thoughtfully curated matches from premium retailers.
              </p>
            </div>

            {/* Glass Upload Card */}
            <div 
              className="card-glass w-full max-w-md p-8 text-center animate-fade-in"
              style={{ animationDelay: '150ms' }}
            >
              {/* Drag & Drop Zone - Works on desktop */}
              <div 
                {...getRootProps()}
                className={`upload-zone w-full mb-6 cursor-pointer ${isDragActive ? 'active' : ''}`}
                data-testid="upload-zone"
              >
                <input {...getInputProps()} />
                <div 
                  className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center transition-transform"
                  style={{ 
                    backgroundColor: isDragActive ? 'var(--accent-primary)' : 'var(--accent-muted)',
                    transform: isDragActive ? 'scale(1.1)' : 'scale(1)'
                  }}
                >
                  <Camera 
                    className="w-7 h-7 transition-colors" 
                    style={{ color: isDragActive ? 'var(--text-inverse)' : 'var(--accent-primary)' }} 
                  />
                </div>
                <p className="text-body mb-2" style={{ color: 'var(--text-primary)' }}>
                  {isDragActive ? "Drop your image here" : "Snap a photo of your clothes"}
                </p>
                <p 
                  className="text-small underline hover:opacity-80 transition-opacity"
                  style={{ color: 'var(--accent-primary)' }}
                >
                  or drag & drop / browse your photos
                </p>
              </div>
              
              {/* Camera Button - Only show on mobile/touch devices */}
              <button 
                onClick={handleOpenCamera}
                className="btn-secondary w-full flex items-center justify-center gap-2 md:hidden"
                data-testid="camera-btn"
              >
                <Camera className="w-4 h-4" />
                Open Camera
              </button>
              
              {/* No account required note */}
              <p 
                className="text-xs text-center mt-4 px-4"
                style={{ color: 'var(--text-muted)' }}
              >
                No account required to start discovering matching styles from your favorite retailers.
              </p>
            </div>

            {/* Camera Modal */}
            <CameraCapture 
              isOpen={isCameraOpen}
              onClose={() => setIsCameraOpen(false)}
              onCapture={handleCameraCapture}
            />
          </div>
        </section>

        {/* How Shadesense Works Section - Responsive Layout */}
        <section className="py-24" style={{ backgroundColor: 'var(--background-secondary)' }}>
          <div className="container">
            <div className="text-center mb-20">
              <h2 className="text-h1 mb-4" style={{ color: 'var(--text-primary)' }}>
                How Shadesense Works
              </h2>
            </div>

            {/* Step 1 - Desktop: Image Left, Text Right | Mobile: Text Above, Image Below */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center mb-24">
              {/* Screenshot Mockup - Shows first on desktop, second on mobile */}
              <div className="order-2 lg:order-1">
                <div 
                  className="rounded-2xl overflow-hidden shadow-xl border"
                  style={{ 
                    backgroundColor: 'var(--surface-primary)', 
                    borderColor: 'var(--border-subtle)'
                  }}
                >
                  {/* Browser Chrome */}
                  <div className="flex items-center gap-2 px-4 py-3" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FF5F56' }} />
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FFBD2E' }} />
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#27C93F' }} />
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="h-6 rounded-md px-3 flex items-center text-xs" style={{ backgroundColor: 'var(--background-primary)', color: 'var(--text-muted)' }}>
                        shadesense.ai
                      </div>
                    </div>
                  </div>
                  {/* App Screenshot Content - Upload Stage */}
                  <div className="p-8" style={{ backgroundColor: 'var(--background-primary)' }}>
                    <div className="text-center mb-6">
                      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--accent-primary)' }}>Your Personal Color Advisor</p>
                      <h3 className="text-lg font-serif" style={{ color: 'var(--text-primary)' }}>Discover perfectly matched clothing</h3>
                    </div>
                    <div 
                      className="border-2 border-dashed rounded-xl p-8 text-center"
                      style={{ borderColor: 'var(--accent-primary)', backgroundColor: 'var(--accent-muted)' }}
                    >
                      <div className="w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--accent-primary)' }}>
                        <Camera className="w-6 h-6" style={{ color: 'var(--text-inverse)' }} />
                      </div>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Snap a photo of your clothes</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>or browse your photos</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Text Content - Shows second on desktop, first on mobile */}
              <div className="order-1 lg:order-2 text-center lg:text-left">
                <h3 className="text-h2 mb-4" style={{ color: 'var(--text-primary)' }}>
                  1. Snap a photo of your clothes
                </h3>
                <p className="text-body text-secondary">
                  Take a photo or browse your gallery. Shadesense accepts photos of any garment — from your favorite jacket to that perfect pair of jeans.
                </p>
              </div>
            </div>

            {/* Step 2 - Desktop: Text Left, Image Right | Mobile: Text Above, Image Below */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center mb-24">
              {/* Text Content - Shows first on both desktop and mobile */}
              <div className="text-center lg:text-left">
                <h3 className="text-h2 mb-4" style={{ color: 'var(--text-primary)' }}>
                  2. Reveal the color palette
                </h3>
                <p className="text-body text-secondary">
                  Our AI analyzes your garment and extracts its precise color palette, identifying dominant shades and supporting tones with color theory accuracy.
                </p>
              </div>
              
              {/* Screenshot Mockup */}
              <div>
                <div 
                  className="rounded-2xl overflow-hidden shadow-xl border"
                  style={{ 
                    backgroundColor: 'var(--surface-primary)', 
                    borderColor: 'var(--border-subtle)'
                  }}
                >
                  {/* Browser Chrome */}
                  <div className="flex items-center gap-2 px-4 py-3" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FF5F56' }} />
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FFBD2E' }} />
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#27C93F' }} />
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="h-6 rounded-md px-3 flex items-center text-xs" style={{ backgroundColor: 'var(--background-primary)', color: 'var(--text-muted)' }}>
                        shadesense.ai/dashboard
                      </div>
                    </div>
                  </div>
                  {/* App Screenshot Content - Color Palette Stage */}
                  <div className="p-6" style={{ backgroundColor: 'var(--background-primary)' }}>
                    <div className="flex gap-4">
                      {/* Garment Image */}
                      <div className="w-32 h-32 rounded-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                        <img 
                          src="https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=200&h=200&fit=crop" 
                          alt="Garment"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {/* Color Analysis */}
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-wider mb-3" style={{ color: 'var(--accent-primary)' }}>Detected Colors</p>
                        <div className="grid grid-cols-4 gap-2 mb-4">
                          {['#2C3E50', '#E74C3C', '#ECF0F1', '#95A5A6'].map((color, i) => (
                            <div key={i} className="aspect-square rounded-lg shadow-sm" style={{ backgroundColor: color }} />
                          ))}
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#2C3E50' }} />
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Navy Blue — Primary</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#E74C3C' }} />
                            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Coral Red — Accent</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 - Desktop: Image Left, Text Right | Mobile: Text Above, Image Below */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-20 items-center">
              {/* Screenshot Mockup - Shows first on desktop, second on mobile */}
              <div className="order-2 lg:order-1">
                <div 
                  className="rounded-2xl overflow-hidden shadow-xl border"
                  style={{ 
                    backgroundColor: 'var(--surface-primary)', 
                    borderColor: 'var(--border-subtle)'
                  }}
                >
                  {/* Browser Chrome */}
                  <div className="flex items-center gap-2 px-4 py-3" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FF5F56' }} />
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FFBD2E' }} />
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#27C93F' }} />
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="h-6 rounded-md px-3 flex items-center text-xs" style={{ backgroundColor: 'var(--background-primary)', color: 'var(--text-muted)' }}>
                        shadesense.ai/dashboard
                      </div>
                    </div>
                  </div>
                  {/* App Screenshot Content - Search Results Stage */}
                  <div className="p-6" style={{ backgroundColor: 'var(--background-primary)' }}>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--accent-primary)' }}>Matching Products</p>
                      <div className="flex gap-2">
                        <div className="px-2 py-1 rounded text-xs" style={{ backgroundColor: 'var(--accent-muted)', color: 'var(--accent-primary)' }}>Exact Match</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                          <div className="aspect-square">
                            <img 
                              src={`https://images.unsplash.com/photo-${i === 1 ? '1591047139829-d91aecb6caea' : i === 2 ? '1434389677669-e08b4cac3105' : '1525507119028-ed4c629a60a3'}?w=150&h=150&fit=crop`}
                              alt="Product"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="p-2">
                            <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>Navy Blazer</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>$89.00</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Text Content - Shows second on desktop, first on mobile */}
              <div className="order-1 lg:order-2 text-center lg:text-left">
                <h3 className="text-h2 mb-4" style={{ color: 'var(--text-primary)' }}>
                  3. Discover matching pieces
                </h3>
                <p className="text-body text-secondary mb-4">
                  Browse curated results from premium retailers. Choose exact color matches or explore complementary pieces that perfectly complete your look.
                </p>
                <p className="text-small italic" style={{ color: 'var(--text-muted)' }}>
                  Some links are affiliate links. We may earn a commission.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Download the App Section */}
        <section className="py-24">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <div className="text-center lg:text-left">
                <p className="text-meta uppercase tracking-widest mb-4" style={{ color: 'var(--accent-primary)' }}>
                  Coming Soon
                </p>
                <h2 className="text-h1 mb-6" style={{ color: 'var(--text-primary)' }}>
                  Download the App
                </h2>
                <p className="text-body text-secondary mb-8 max-w-md mx-auto lg:mx-0">
                  Take Shadesense with you wherever you go. Analyze colors, discover matches, and build your perfect wardrobe — all from your phone.
                </p>

                {/* App Store Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  {/* Apple App Store */}
                  <a 
                    href="#" 
                    className="inline-flex items-center gap-3 px-6 py-3 rounded-xl transition-all hover:scale-105"
                    style={{ backgroundColor: 'var(--text-primary)', color: 'var(--text-inverse)' }}
                  >
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs opacity-80">Download on the</p>
                      <p className="text-lg font-medium -mt-1">App Store</p>
                    </div>
                  </a>

                  {/* Google Play */}
                  <a 
                    href="#" 
                    className="inline-flex items-center gap-3 px-6 py-3 rounded-xl transition-all hover:scale-105"
                    style={{ backgroundColor: 'var(--text-primary)', color: 'var(--text-inverse)' }}
                  >
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs opacity-80">GET IT ON</p>
                      <p className="text-lg font-medium -mt-1">Google Play</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Phone Mockup - Reduced Size */}
              <div className="flex justify-center lg:justify-end">
                <div className="relative">
                  {/* Phone Frame - Half size */}
                  <div 
                    className="w-32 h-[260px] rounded-[20px] p-1.5 shadow-xl"
                    style={{ backgroundColor: 'var(--text-primary)' }}
                  >
                    {/* Screen */}
                    <div 
                      className="w-full h-full rounded-[16px] overflow-hidden relative"
                      style={{ backgroundColor: 'var(--background-primary)' }}
                    >
                      {/* Status Bar */}
                      <div 
                        className="h-4 flex items-center justify-center"
                        style={{ backgroundColor: 'var(--surface-primary)' }}
                      >
                        <div className="w-10 h-2.5 rounded-full" style={{ backgroundColor: 'var(--text-primary)' }} />
                      </div>
                      
                      {/* App Content */}
                      <div className="p-2">
                        <img 
                          src={LOGO_URL} 
                          alt="Shadesense" 
                          className="h-4 w-auto mb-2"
                        />
                        <div 
                          className="aspect-square rounded-lg mb-2 overflow-hidden"
                          style={{ backgroundColor: 'var(--surface-secondary)' }}
                        >
                          <img 
                            src="https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=200&h=200&fit=crop" 
                            alt="App screenshot"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex gap-1 mb-2">
                          {['#E6C89A', '#8B7355', '#2F4F4F', '#F5F5DC'].map((color, i) => (
                            <div 
                              key={i}
                              className="w-5 h-5 rounded"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        <div 
                          className="h-1.5 rounded-full w-3/4 mb-1"
                          style={{ backgroundColor: 'var(--surface-secondary)' }}
                        />
                        <div 
                          className="h-1.5 rounded-full w-1/2"
                          style={{ backgroundColor: 'var(--surface-secondary)' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Logo & Copyright */}
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Shadesense.ai" className="h-8 w-auto" />
              <p className="text-meta text-muted">
                © {new Date().getFullYear()} Shadesense Inc. All rights reserved.
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap items-center gap-6">
              <Link to="/terms" className="text-small text-secondary hover:text-primary transition-colors">
                Terms of Use
              </Link>
              <Link to="/privacy" className="text-small text-secondary hover:text-primary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookies" className="text-small text-secondary hover:text-primary transition-colors">
                Cookie Policy
              </Link>
              <Link to="/policies" className="text-small text-secondary hover:text-primary transition-colors">
                Other Policies & Disclosures
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
